import React, { useEffect, useState } from 'react';
import SearchableSelect from './SearchableSelect';

interface CountryEntry {
  name: string;
  iso: string;
}

interface ResidencePickerProps {
  city: string;
  country: string;
  onChange: (value: { city: string; country: string }) => void;
}

// Cache the small countries list across mounts.
let countriesCache: CountryEntry[] | null = null;

/**
 * Country + City picker backed by static per-country JSON (generated by scripts/generate-geo.js).
 * Country list (~8 KB) loads once; a country's cities (a few KB, up to ~185 KB) load only when picked.
 * Nothing here is in the main bundle — it fetches on demand, so it never slows initial load.
 */
const ResidencePicker: React.FC<ResidencePickerProps> = ({ city, country, onChange }) => {
  const [countries, setCountries] = useState<CountryEntry[]>(countriesCache || []);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Load the countries list once.
  useEffect(() => {
    if (countriesCache) return;
    let active = true;
    fetch('/geo/countries.json')
      .then((r) => r.json())
      .then((data: CountryEntry[]) => {
        countriesCache = data;
        if (active) setCountries(data);
      })
      .catch(() => setCountries([]));
    return () => {
      active = false;
    };
  }, []);

  const iso = countries.find((c) => c.name === country)?.iso;

  // Load the selected country's cities on demand.
  useEffect(() => {
    if (!iso) {
      setCities([]);
      return;
    }
    let active = true;
    setLoadingCities(true);
    fetch(`/geo/cities/${iso}.json`)
      .then((r) => r.json())
      .then((list: string[]) => {
        if (active) setCities(list);
      })
      .catch(() => {
        if (active) setCities([]);
      })
      .finally(() => {
        if (active) setLoadingCities(false);
      });
    return () => {
      active = false;
    };
  }, [iso]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Country</label>
        <SearchableSelect
          options={countries.map((c) => c.name)}
          value={country}
          onChange={(v) => onChange({ country: v, city: '' })}
          placeholder="Select country"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">City</label>
        <SearchableSelect
          options={cities}
          value={city}
          disabled={!country}
          loading={loadingCities}
          onChange={(v) => onChange({ country, city: v })}
          placeholder={country ? 'Select city' : 'Select a country first'}
          emptyText="No match — pick the nearest city"
        />
      </div>
    </div>
  );
};

export default ResidencePicker;
